import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
	TOO_LONG_CODE,
	elementRefusalOf,
	hasElementChoices,
	withElementChoice,
	withElementChoicesField,
} from '../utils/elementRefusal';

/**
 * The person's answers to element refusals, for one window that saves text
 * (element ids step 3, KPR-238 P4; utils/elementRefusal.js).
 *
 *   const picks = useElementChoices();
 *   try { await save(picks.withChoices(payload)); picks.reset(); }
 *   catch (error) { if (picks.take(error)) return; … }
 *   <QueryRefusalPick inline refusal={picks.refusal} onPick={value => { picks.pick(value); save(); }} />
 *
 * `take(errorOrBody)` keeps an `ambiguous_element` or `unknown_element` (and
 * says whether it did); `pick(value)` answers it and returns the choices,
 * which `withChoices` adds to the NEXT request at once — a ref, so a save
 * called in the same handler sends them. The picks stay while the window is
 * open (a second refusal, or a duplicate name, must not send the unpicked
 * text again); `reset` forgets them — when it closes, or its text changes.
 *
 * For a save that is one function of the picks — `attempt(choices => save(…))`
 * runs it with the picks so far and remembers it; `retry(value)` answers the
 * refusal and runs the remembered save again with the new picks:
 *
 *   const submit = choices => run(() => saveGrant(…, { elementChoices: choices }), …, picks.take);
 *   <Button onClick={() => picks.attempt(submit)}>Add</Button>
 *   <QueryRefusalPick inline refusal={picks.refusal} onPick={picks.retry} />
 *
 * A PICK ANSWERS EXACTLY THE TEXT IT WAS MADE FOR, never a later one (the P4
 * review's R-SH1: an old pick would silently answer a new condition, and the
 * person would never be asked). So the picks are forgotten whenever that
 * text changes: `useElementChoices(text)` — the text (or a key of every
 * text) the window's save sends — resets them when it moves, and
 * `attempt(fn, { text })` when a save is made for another text than the
 * last one.
 */
const picksOrNothing = choices => (hasElementChoices(choices) ? choices : undefined);

export default function useElementChoices(text = undefined) {
	const choicesRef = useRef({});
	const attemptRef = useRef(null);
	// The text the remembered save sends (`attempt(fn, { text })`), for a
	// pick that reads it (who-sees-what text, accessPickScope).
	const textRef = useRef(null);
	const [refusal, setRefusal] = useState(null);

	const take = useCallback((errorOrBody) => {
		const body = errorOrBody?.response ? (errorOrBody.response.status === 400 ? errorOrBody.response.data : null) : errorOrBody;
		const next = elementRefusalOf(body);
		if (next) {
			setRefusal(next);
			return true;
		}
		// The picks made it too long (a group repeats its condition; at most
		// 50 conditions): said with the server's sentence, and the picks are
		// forgotten, so the next try asks again (`repick`).
		if (body && (body.code === TOO_LONG_CODE || body.cause === TOO_LONG_CODE) && hasElementChoices(choicesRef.current)) {
			choicesRef.current = {};
			setRefusal({ code: TOO_LONG_CODE, message: String(body.message || ''), field: String(body.field || '') });
			return true;
		}
		return false;
	}, []);

	const pick = useCallback((value, answering = null) => {
		const target = answering || refusal;
		if (!target) return choicesRef.current;
		choicesRef.current = withElementChoice(choicesRef.current, target, value);
		setRefusal(null);
		return choicesRef.current;
	}, [refusal]);

	const reset = useCallback(() => {
		choicesRef.current = {};
		attemptRef.current = null;
		setRefusal(null);
	}, []);

	const attempt = useCallback((fn, { text: sent = null } = {}) => {
		attemptRef.current = fn;
		// Another text than the picks answered: they answer nothing here.
		if (sent !== null && textRef.current !== null && sent !== textRef.current) choicesRef.current = {};
		textRef.current = sent;
		// A new try answers to itself: the last refusal goes until it comes again.
		setRefusal(null);
		return fn(picksOrNothing(choicesRef.current));
	}, []);

	const retry = useCallback((value) => {
		const next = pick(value);
		return typeof attemptRef.current === 'function' ? attemptRef.current(picksOrNothing(next)) : undefined;
	}, [pick]);

	// After a too-long pick: the remembered save again, without picks — the
	// server asks again.
	const repick = useCallback(() => {
		setRefusal(null);
		return typeof attemptRef.current === 'function' ? attemptRef.current(undefined) : undefined;
	}, []);

	const clearRefusal = useCallback(() => setRefusal(null), []);

	// The picks so far — undefined while there are none, so a request carries
	// no `elementChoices` key it does not need.
	const choices = useCallback(() => picksOrNothing(choicesRef.current), []);

	const textOf = useCallback(() => textRef.current, []);

	// The text the window's save sends moved: the picks it held answer nothing.
	useEffect(() => {
		if (text === undefined) return;
		choicesRef.current = {};
		setRefusal(null);
	}, [text]);

	const withChoices = useCallback(payload => withElementChoicesField(payload, choicesRef.current), []);

	return useMemo(
		() => ({ refusal, take, pick, reset, clearRefusal, choices, withChoices, attempt, retry, repick, text: textOf }),
		[refusal, take, pick, reset, clearRefusal, choices, withChoices, attempt, retry, repick, textOf]
	);
}
